import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'

import { useAuthStore } from '@/stores/useAuthStore'
import { ROUTES } from '@/constants/routes'
import { logger } from '@/lib/logger'

declare global {
    interface Window {
        google?: {
            accounts: {
                id: {
                    initialize: (config: Record<string, unknown>) => void
                    renderButton: (element: HTMLElement, config: Record<string, unknown>) => void
                }
            }
        }
    }
}

interface GoogleLoginButtonProps {
    text?: 'signin_with' | 'signup_with' | 'continue_with'
}

export function GoogleLoginButton({ text = 'continue_with' }: GoogleLoginButtonProps) {
    const navigate = useNavigate()
    const buttonRef = useRef<HTMLDivElement>(null)
    const initialized = useRef(false)

    useEffect(() => {
        if (initialized.current || !buttonRef.current) return

        const handleCredentialResponse = async (response: { credential: string }) => {
            const result = await useAuthStore.getState().googleLogin(response.credential)
            if (result === 'success') {
                toast.success('Login successful')
                navigate(ROUTES.home)
            }
        }

        const renderGoogleButton = () => {
            if (!window.google || !buttonRef.current || initialized.current) return

            window.google.accounts.id.initialize({
                client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
                callback: handleCredentialResponse,
                auto_select: false,
            })

            window.google.accounts.id.renderButton(buttonRef.current, {
                type: 'standard',
                shape: 'rectangular',
                theme: 'outline',
                text,
                size: 'large',
                width: 350,
                logo_alignment: 'left',
            })

            initialized.current = true
        }

        if (window.google) {
            renderGoogleButton()
        } else {
            const interval = setInterval(() => {
                if (window.google) {
                    clearInterval(interval)
                    renderGoogleButton()
                }
            }, 100)
            const timeout = setTimeout(() => clearInterval(interval), 5000)
            return () => {
                clearInterval(interval)
                clearTimeout(timeout)
            }
        }
    }, [text, navigate])

    const isLoading = useAuthStore((s) => s.isLoading)

    if (!import.meta.env.VITE_GOOGLE_CLIENT_ID) {
        logger.warn('VITE_GOOGLE_CLIENT_ID not configured')
        return null
    }

    return (
        <div
            ref={buttonRef}
            style={{
                display: 'flex',
                justifyContent: 'center',
                width: '100%',
                minHeight: 44,
                opacity: isLoading ? 0.6 : 1,
                pointerEvents: isLoading ? 'none' : 'auto',
            }}
        />
    )
}
